import type { Role } from '@travel-planner/shared/schemas/auth';
import { formatRoleLabel } from './role-options';

// A role's governance group. Eleggia DMC roles are the consortium partner-profile
// roles from the Tech Spec §3 (HQ Admin, HQ Finance/Legal, DMC Principal, DMC
// Editor, Consortium Member); everything else is a platform role.
export type RoleGroupId = 'platform' | 'eleggia';

export type RoleGroup = {
  id: RoleGroupId;
  label: string;
  hint: string;
};

// One entry per Eleggia DMC role. `description` is the plain-language capability
// from Tech Spec §3; `clearance` is the field-level read tier the role confers
// (mirrors `dmcReadClearance` in @travel-planner/shared/schemas). Kept as UI copy
// only — the server remains the authoritative gate.
type RoleMeta = {
  group: RoleGroupId;
  description?: string;
  clearance?: 'L1' | 'L2' | 'L3';
};

const ROLE_META: Partial<Record<Role, RoleMeta>> = {
  hq_admin: {
    group: 'eleggia',
    clearance: 'L3',
    description:
      'Full read on all profiles; edits HQ-owned fields; manages roles and publishes aggregates.',
  },
  hq_finance_legal: {
    group: 'eleggia',
    clearance: 'L3',
    description:
      'Reads restricted (L3) fields across profiles — vault references, shareholding, revenue bands. No edit.',
  },
  dmc_principal: {
    group: 'eleggia',
    clearance: 'L3',
    description:
      'Full read/edit of own profile including L3; approves submissions and assigns DMC Editors.',
  },
  dmc_editor: {
    group: 'eleggia',
    clearance: 'L2',
    description:
      'Edits own profile at L1/L2 as delegated per module. No L3 access.',
  },
  consortium_member: {
    group: 'eleggia',
    clearance: 'L1',
    description:
      "Read-only directory view of other members' L1 (public) fields.",
  },
};

export const ROLE_GROUPS: RoleGroup[] = [
  {
    id: 'platform',
    label: 'Platform roles',
    hint: 'Xthory application access (dashboard, itineraries, products, …).',
  },
  {
    id: 'eleggia',
    label: 'Eleggia DMC roles',
    hint: 'Consortium partner-profile governance and field-level access (L1/L2/L3).',
  },
];

export type RoleOptionMeta = {
  value: Role;
  label: string;
  description?: string;
  clearance?: 'L1' | 'L2' | 'L3';
};

// Roles for a group, in the platform-then-eleggia order the enum defines them,
// each carrying its display label + (for Eleggia roles) description + clearance.
export const roleOptionsByGroup = (
  allRoles: readonly Role[],
  group: RoleGroupId
): RoleOptionMeta[] =>
  allRoles
    .filter((r) => (ROLE_META[r]?.group ?? 'platform') === group)
    .map((r) => ({
      value: r,
      label: formatRoleLabel(r),
      description: ROLE_META[r]?.description,
      clearance: ROLE_META[r]?.clearance,
    }));
